import { Injectable, computed, effect, signal } from '@angular/core';
import {
  AccountType,
  AgentApplication,
  KycStatus,
  OnboardingEntity,
  OnboardingProfile,
  IdDocumentType,
  OnboardingState,
  OnboardingStep,
  SignupDocument,
  SignupDocumentSource,
  SignupCompanyFacts,
  SignupPerson,
  initialOnboardingState,
} from '../models/onboarding.models';

// v2: the step numbers changed (Phone became its own screen, Agent left the flow), so progress
// saved under the old numbers must not be restored onto the wrong screen.
const STORAGE_KEY = 'bc_onboarding_state_v2';

/**
 * Single signal store holding the cumulative registration payload across screens.
 * Persisted to sessionStorage so a refresh / back-nav doesn't wipe progress.
 */
@Injectable({ providedIn: 'root' })
export class OnboardingStateService {
  private readonly _state = signal<OnboardingState>(this.restore());

  /** Transient password — held in memory only, never persisted to sessionStorage. */
  private readonly _password = signal<string>('');

  /**
   * Documents uploaded on "Your details", held in memory until the application is created and they
   * are filed in its DMS. Files can't go into sessionStorage, so a page reload drops them.
   */
  private readonly _documents = signal<Partial<Record<SignupDocumentSource, SignupDocument[]>>>({});
  readonly documents = computed(() => Object.values(this._documents()).flat());

  /** Read-only view of the whole store. */
  readonly state = this._state.asReadonly();

  // Convenience selectors used by step components / the shell.
  readonly step = computed(() => this._state().step);
  readonly accountType = computed(() => this._state().accountType);
  readonly isBusiness = computed(() => this._state().accountType === 'business');
  readonly kycStatus = computed(() => this._state().kycStatus);
  readonly emailVerified = computed(() => this._state().emailVerified);

  constructor() {
    // Persist every change. sessionStorage survives refresh but clears on tab close.
    effect(() => {
      const snapshot = this._state();
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
      } catch {
        /* storage may be unavailable (private mode / SSR) — non-fatal */
      }
    });
  }

  // ── Mutations ───────────────────────────────────────────────────────────

  patch(partial: Partial<OnboardingState>): void {
    this._state.update((s) => ({ ...s, ...partial }));
  }

  goToStep(step: OnboardingStep): void {
    this.patch({ step });
  }

  next(): void {
    this.patch({ step: this.nextStep(this._state()) });
  }

  back(): void {
    this.patch({ step: this.prevStep(this._state()) });
  }

  setEmail(email: string): void {
    this.patch({ email: email.trim().toLowerCase() });
  }

  setEmailVerified(verified: boolean): void {
    this.patch({ emailVerified: verified });
  }

  setPhone(phone: string, verified = false): void {
    this.patch({ phone, phoneVerified: verified });
  }

  setPhoneVerified(verified: boolean): void {
    this.patch({ phoneVerified: verified });
  }

  /** Password is kept out of the persisted state. */
  setPassword(password: string): void {
    this._password.set(password);
  }

  get password(): string {
    return this._password();
  }

  clearPassword(): void {
    this._password.set('');
  }

  setAccountType(accountType: AccountType): void {
    this.patch({ accountType });
  }

  setPersonal(firstName: string, lastName: string, greek?: { firstName?: string; lastName?: string }): void {
    this.patch({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      firstNameGreek: greek?.firstName?.trim() || undefined,
      lastNameGreek: greek?.lastName?.trim() || undefined,
    });
  }

  /** Individuals: what recognises an existing client besides the name. */
  setIdentity(dateOfBirth: string, idType: IdDocumentType, idNumber: string): void {
    this.patch({ dateOfBirth, idType, idNumber: idNumber.trim() });
  }

  /** What a document read on "Your details" says beyond the fields shown there. */
  setDocumentFacts(facts: { gender?: string; nationality?: string; idExpiry?: string; idIssuingCountry?: string }): void {
    const s = this._state();
    this.patch({
      gender: facts.gender || s.gender,
      nationality: facts.nationality || s.nationality,
      idExpiry: facts.idExpiry || s.idExpiry,
      idIssuingCountry: facts.idIssuingCountry || s.idIssuingCountry,
    });
  }

  /**
   * The documents a reading came from. A new upload replaces the previous one from the same panel
   * (the person's ID, or the company's papers), never the other's.
   */
  setDocuments(source: SignupDocumentSource, documents: SignupDocument[]): void {
    this._documents.update((all) => ({ ...all, [source]: [...documents] }));
  }

  /** The people from the company's papers and the answers given about them; [] clears them. */
  setCompanyPeople(people: SignupPerson[], facts?: SignupCompanyFacts): void {
    this.patch({ companyPeople: people.map((p) => ({ ...p })), companyFacts: facts });
  }

  setRoles(roles: string[]): void {
    this.patch({ roles: [...roles] });
  }

  setEntity(entity: OnboardingEntity | undefined): void {
    this.patch({ entity });
  }

  setAzureObjectId(azureObjectId: string): void {
    this.patch({ azureObjectId });
  }

  setProfile(profile: OnboardingProfile): void {
    this.patch({ profile });
  }

  setOnboardingId(onboardingId: string): void {
    this.patch({ onboardingId });
  }

  /** Never takes the outcome: the browser only knows the check ran, and its support reference. */
  setKyc(status: KycStatus, reference?: string): void {
    this.patch({ kycStatus: status, kycReference: reference });
  }

  setTermsAccepted(accepted: boolean): void {
    this.patch({ termsAccepted: accepted });
  }

  setBecomeAgent(becomeAgent: boolean, application?: AgentApplication): void {
    this.patch({ becomeAgent, agentApplication: application });
  }

  reset(): void {
    this._state.set({ ...initialOnboardingState });
    this._password.set('');
    this._documents.set({});
    // Clear the persisted copy synchronously so a logout redirect that fires
    // before the effect flushes still leaves a clean slate for the next session.
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* storage unavailable — non-fatal */
    }
  }

  // ── Step transitions ──────────────────────────────────────────────────────

  /** Linear progression; the Details screen branches individual/business inline. */
  private nextStep(s: OnboardingState): OnboardingStep {
    if (s.step >= OnboardingStep.Complete) return OnboardingStep.Complete;
    return (s.step + 1) as OnboardingStep;
  }

  private prevStep(s: OnboardingState): OnboardingStep {
    // Account Type is the first in-app step (B2C owns everything before it).
    if (s.step <= OnboardingStep.AccountType) return OnboardingStep.AccountType;
    return (s.step - 1) as OnboardingStep;
  }

  // ── Persistence ─────────────────────────────────────────────────────────

  private restore(): OnboardingState {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) {
        const restored = { ...initialOnboardingState, ...(JSON.parse(raw) as OnboardingState) };
        // Older builds kept the screening's declined reason here; drop it rather than carry it on.
        delete (restored as unknown as { kycDeclinedReason?: unknown }).kycDeclinedReason;
        // B2C owns account creation, so never resume on the email/verify/password
        // steps — clamp any stale stored step up to Account Type.
        if (restored.step < OnboardingStep.AccountType) {
          restored.step = OnboardingStep.AccountType;
        }
        return restored;
      }
    } catch {
      /* ignore corrupt / unavailable storage */
    }
    return { ...initialOnboardingState };
  }
}
